import type { BOQ, BOQItem, BOQSection } from '@/types/api/sites.types';

/**
 * Flattens a BOQ's nested section tree into a single list of line items, tagging each with the
 * section trail it came from so a flat list (a search dropdown, a table) can still show where
 * an item lives.
 *
 * A BOQ nests arbitrarily: sections contain items, item *groups* (which contain their own
 * items), and further subsections. Consumers that needed a flat list used to re-walk that tree
 * inline — `Orders/BOQItemSearch.vue` still carries its own copy. This is that walk, once.
 *
 * **Items without a `specification` are dropped.** Carried over from the legacy searches: an
 * unspecified line isn't orderable, so it must not be offered as a choice.
 *
 * The three trail fields on `BOQItem` map as:
 * - `sectionName` — the **top-level** section, whatever the depth.
 * - `subSectionName` — the intermediate subsections between that and the item, joined with
 *   `' / '`; `null` when the item sits directly on a top-level section.
 * - `groupName` — the item group, when the item came from one.
 *
 * Note this differs from the legacy walks, which passed only the immediate parent pair down and
 * so *lost* the top-level section name three levels deep (an item's `sectionName` became its
 * grandparent subsection). Two-level BOQs — nearly all of them — flatten identically.
 */
export const flattenBoqLineItems = (boq: BOQ | undefined | null): BOQItem[] => {
  if (!boq?.sections) return [];

  return boq.sections.flatMap((section) => flattenSection(section, []));
};

/**
 * @param ancestors Section names from the top level down to (not including) `section`.
 */
const flattenSection = (section: BOQSection, ancestors: string[]): BOQItem[] => {
  const trail = [...ancestors, section.name];

  // The top-level section is the head of the trail; everything between it and the item is the
  // sub-section trail.
  const [sectionName = '', ...subSections] = trail;
  const subSectionName = subSections.length > 0 ? subSections.join(' / ') : null;

  const isOrderable = (item: BOQItem) => Boolean(item.specification);

  const directItems = (section.items ?? [])
    .filter((item) => isOrderable(item))
    .map((item) => ({
      ...item,
      sectionName,
      subSectionName,
      groupName: null,
    }));

  const groupedItems = (section.itemGroups ?? []).flatMap((group) =>
    (group.items ?? [])
      .filter((item) => isOrderable(item))
      .map((item) => ({
        ...item,
        sectionName,
        subSectionName,
        groupName: group.name,
      })),
  );

  const nestedItems = (section.sections ?? []).flatMap((subsection) =>
    flattenSection(subsection, trail),
  );

  return [...directItems, ...groupedItems, ...nestedItems];
};

/**
 * Every line on the BOQ, untagged and unfiltered.
 *
 * Deliberately **not** `flattenBoqLineItems`: that one drops lines with no specification, because
 * an unspecified line cannot be offered as a choice. This is for the questions asked *about* the
 * bill rather than about what can be picked from it — whether it carries a line nothing can be
 * ordered against, or which line an id refers to — and there a line being unorderable for one
 * reason must not hide that it is also unorderable for another (PLE-1266).
 */
export const collectBoqLineItems = (boq: BOQ | undefined | null): BOQItem[] =>
  (boq?.sections ?? []).flatMap((section) => collectSectionLineItems(section));

const collectSectionLineItems = (section: BOQSection): BOQItem[] => [
  ...(section.items ?? []),
  ...(section.itemGroups ?? []).flatMap((group) => group.items ?? []),
  ...(section.sections ?? []).flatMap((subsection) => collectSectionLineItems(subsection)),
];

/**
 * Whether any line on the BOQ has lost its vendor item. What blocks the bill being submitted for
 * approval or approved: a bill that commits to buying something nobody sells is not a bill anyone
 * can sign off, so the line has to be replaced first (PLE-1266).
 */
export const hasUnavailableLineItem = (boq: BOQ | undefined | null): boolean =>
  collectBoqLineItems(boq).some((item) => item.vendorItemUnavailable);

/**
 * A line's place in the BOQ, as a trail: `Section / Sub-section / Group`.
 *
 * Levels the line doesn't have are dropped rather than rendered empty, so a line sitting directly
 * under a section reads as that section alone rather than as a path with holes in it.
 */
export const boqSectionPath = (item: BOQItem): string =>
  [item.sectionName, item.subSectionName, item.groupName].filter(Boolean).join(' / ');

/**
 * The section present after a create that wasn't there before it.
 *
 * `createSection` returns the whole BOQ rather than the row it added, so the caller has to work out
 * which one is new. Matching on name is what it looked like first, and it's wrong twice over: BOQ
 * section names aren't unique, so an existing section with the same title is returned instead — and
 * when the create fails and the BOQ never changes, that same match reports success. Both put the
 * line under a section the user didn't create, silently.
 *
 * Identity is the only reliable answer. Returns `undefined` when nothing was added, which is the
 * honest result for a create that didn't happen.
 */
export const findAddedSection = (
  before: BOQSection[] = [],
  after: BOQSection[] = [],
): BOQSection | undefined => {
  const existingIds = new Set(before.map((section) => section.id));

  return after.find((section) => !existingIds.has(section.id));
};
